/**
 * Dependencies.
 */
import * as L from 'leaflet';
import { icon, LatLngTuple, Map, MapOptions, TileLayerOptions } from 'leaflet';
import * as LFilter from 'leaflet.tilelayer.colorfilter';
import { isNumber } from 'lodash-es';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { LControl, LMap, LMarker, LPopup, LTileLayer } from 'vue2-leaflet';

import {
  calculateDistanceBetweenTwoPoints,
  popupRenderer
} from '@/modules/main/utils/map';

/**
 * Interface `ICoordinates`.
 */
interface ICoordinates {
  latitude: number;
  longitude: number;
  direction?: number;
}

/**
 * Component.
 */
@Component({
  components: {
    LControl,
    LMap,
    LMarker,
    LPopup,
    LTileLayer
  },
  name: 'pick-up-points-component'
})
export default class extends Vue {
  /**
   * Property `L`.
   */
  L = L;

  /**
   * Property `leafletMap`.
   */
  leafletMap: Map = null;

  /**
   * Property `originalMarkerPoint`.
   */
  originalMarkerPoint = null;

  /**
   * Property `newMarkerPin`.
   */
  newMarkerPin = require('@/assets/marker-new-address.svg');

  /**
   * Property `originalMarkerPin`.
   */
  originalMarkerPin = require('@/assets/marker.svg');

  /**
   * Property `nearestMarker`.
   */
  nearestMarker = null;

  /**
   * Property `originalMarker`.
   */
  @Prop({ default: null })
  originalMarker: ICoordinates;

  /**
   * Property `pickUpPointsMarkers`.
   */
  @Prop({ default: () => [] })
  pickUpPointsMarkers: {
    id: number;
    code: string;
    coordinates: LatLngTuple;
    hasDockingDay: boolean;
    hasRadiusDistance: boolean;
    hasWorkingDay: boolean;
    name: string;
  }[];

  /**
   * Getter `originalMarkerCoordinates`.
   */
  get originalMarkerCoordinates(): LatLngTuple {
    const { latitude = null, longitude = null } = this.originalMarker || {};

    return isNumber(latitude) && isNumber(longitude)
      ? [latitude, longitude]
      : null;
  }

  /**
   * Getter `options`.
   */
  get options(): MapOptions {
    return {
      attributionControl: false,
      center: [0, 0],
      maxBounds: [
        [-90, -180],
        [90, 180]
      ],
      minZoom: 2,
      zoom: 2
    };
  }

  /**
   * Getter `tileUrl`.
   */
  get tileUrl() {
    return 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  }

  /**
   * Getter `tileLayerOptions`.
   */
  get tileLayerOptions(): TileLayerOptions {
    return {
      referrerPolicy: 'strict-origin-when-cross-origin'
    };
  }

  /**
   * Getter `newMarkerIcon`.
   */
  get newMarkerIcon() {
    const iconUrl = this.newMarkerPin;

    return icon({
      iconAnchor: [32, 49],
      iconSize: [64, 64],
      iconUrl
    });
  }

  /**
   * Getter `originalMarkerIcon`.
   */
  get originalMarkerIcon() {
    const iconUrl = this.originalMarkerPin;

    return icon({
      iconAnchor: [32, 49],
      iconSize: [64, 64],
      iconUrl
    });
  }

  /**
   * Handle map ready.
   */
  handleMapReady(mapObject) {
    this.leafletMap = mapObject;
    this.originalMarkerPoint = this.$refs['originalMarkerPoint'];

    this.handleMapGrayFilter();
    this.handleAutoFit();
    this.handleSetTooltips();
  }

  /**
   * Handle map gray filter.
   */
  handleMapGrayFilter() {
    if (!window.hasOwnProperty('LFilter')) {
      window['LFilter'] = LFilter;
    }

    const myFilter = [
      'invert:20%',
      'grayscale:100%',
      'bright:130%',
      'contrast:100%',
      'saturate:0%',
      'sepia:2%'
    ];
    (L.tileLayer as any)
      .colorFilter('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        filter: myFilter,
        id: 'osm.mapnik'
      })
      .addTo(this.leafletMap);
  }

  /**
   * Handle auto fit coordinates.
   */
  handleAutoFit() {
    if (this.leafletMap) {
      // Markers.
      let latlngs: LatLngTuple[] = [];

      if (this.originalMarkerCoordinates) {
        latlngs.push(this.originalMarkerCoordinates);
      }

      this.nearestMarker = null;

      let lastShorterDistance = null;
      if (this.originalMarkerCoordinates && this.pickUpPointsMarkers.length) {
        this.pickUpPointsMarkers.forEach(marker => {
          const { coordinates: markerCoordinates } = marker || {};
          const distance = calculateDistanceBetweenTwoPoints({
            marker1: this.originalMarkerCoordinates,
            marker2: markerCoordinates
          });

          if (
            !lastShorterDistance ||
            (distance > 0 && distance < lastShorterDistance)
          ) {
            lastShorterDistance = distance;
            this.nearestMarker = marker;
          }
        });
      }

      if (this.nearestMarker) {
        const { coordinates } = this.nearestMarker || {};
        latlngs = [...latlngs, coordinates];
      }

      // Fit bounds by client address and nearest pickup marker.
      this.leafletMap.fitBounds(latlngs, { padding: [64, 64] });
    }
  }

  /**
   * Handle set tooltips.
   */
  handleSetTooltips() {
    this.pickUpPointsMarkers.map(({ name = null }, index) => {
      const markerPoint = this.$refs[`pickUpMarkerPoint${index}`][0];
      const content = popupRenderer({ index, name });

      if (name) {
        markerPoint.mapObject.bindPopup(content, { offset: [0, -32] });
      }
    });
  }

  /**
   * Handle open popup.
   */
  handleOpenPopup({ id, index }: { id: number; index: number }) {
    const markerPoint = this.$refs[`pickUpMarkerPoint${index}`][0];

    this.$nextTick(() => {
      markerPoint.mapObject.openPopup();

      const popupElement = document.getElementById(`pickupPopup${index}`);

      if (popupElement) {
        popupElement.addEventListener(
          'click',
          this.handlePickUpMarkerClick.bind(null, { id })
        );
      }
    });
  }

  /**
   * Handle pick up marker click.
   */
  handlePickUpMarkerClick({ id }) {
    this.$emit('select', { id });
  }
}
